import isNil from "lodash/isNil";

import { ExploreTestID, TextTestID } from "@/constants/TestID";
import { IllustrationSize } from "@/constants/UI";
import CoreTag from "@/ui/components/groupClasses/series/CoreTag";
import Text from "@/ui/core/Text";
import { BaseCard, Box, HStack, VStack } from "@/ui/layout";
import { getIllustrationIcon } from "@/ui/theme/icons/illustrations/groupClasses";
import { ColorToken } from "@/ui/theme/tokens";
import { joinTestIDs, pluralize } from "@/utils/string.util";

type GroupClassCardSize = "default" | "small";
const CardWidths: Record<GroupClassCardSize, number | undefined> = {
  default: undefined,
  small: 257,
};

interface GroupClassCardProps {
  description?: string;
  groupClassExternalId: string;
  iconName?: string;
  isCore?: boolean;
  isRegistered?: boolean;
  nextDateString?: string;
  numberOfOptions?: number;
  onSelect: (groupClassExternalId: string) => void;
  recurrenceString?: string;
  size?: GroupClassCardSize;
  title: string;
}

// view-model for a card in a list; the list supplies the press handler + sizing separately.
// Declared independently of GroupClassCardProps so the data shape isn't coupled to the component.
interface GroupClassCardItem {
  description?: string;
  // the registered section this card represents; a user can hold multiple sections of the
  // same group, so this distinguishes those rows as the list key (and will drive
  // section-scoped navigation). Undefined for available/recommended cards (one row per group).
  groupSectionId?: string;
  iconName?: string;
  // groupId — used to navigate to group details (the details screen queries by group)
  id: string;
  isCore?: boolean;
  isRegistered?: boolean;
  nextDateString?: string;
  numberOfOptions?: number;
  recurrenceString?: string;
  title: string;
}

type RegisteredFooterProps = { baseTestID: string } & Pick<
  GroupClassCardProps,
  "nextDateString" | "recurrenceString"
>;

const RegisteredFooter = ({
  baseTestID,
  nextDateString,
  recurrenceString,
}: RegisteredFooterProps) => (
  <VStack gap={2}>
    {nextDateString && (
      <Text
        testID={joinTestIDs(baseTestID, ExploreTestID.GroupClassCardNextDate)}
        variant="bodyMedium"
      >
        {nextDateString}
      </Text>
    )}
    {recurrenceString && (
      <Text
        color={ColorToken.NeutralSecondary}
        testID={joinTestIDs(baseTestID, ExploreTestID.GroupClassCardRecurrence)}
        variant="caption"
      >
        {recurrenceString}
      </Text>
    )}
  </VStack>
);

type OptionsFooterProps = { baseTestID: string } & Pick<
  GroupClassCardProps,
  "numberOfOptions"
>;

const OptionsFooter = ({ baseTestID, numberOfOptions }: OptionsFooterProps) => {
  if (isNil(numberOfOptions)) return null;

  return (
    <Text
      color={ColorToken.Primary100}
      testID={joinTestIDs(baseTestID, ExploreTestID.GroupClassCardOptions)}
      variant="bodyMedium"
    >
      {`${numberOfOptions} available option${pluralize({ itemCount: numberOfOptions })}`}
    </Text>
  );
};

const GroupClassCard = ({
  description,
  groupClassExternalId,
  iconName,
  isCore,
  isRegistered,
  nextDateString,
  numberOfOptions,
  onSelect,
  recurrenceString,
  size = "default",
  title,
}: GroupClassCardProps) => {
  const IllustrationIcon = iconName
    ? getIllustrationIcon({
        height: IllustrationSize.Card,
        iconName,
        width: IllustrationSize.Card,
      })
    : null;

  const baseTestID = joinTestIDs(
    groupClassExternalId,
    ExploreTestID.GroupClassCard,
  );

  return (
    <BaseCard
      maxWidth={CardWidths[size]}
      onPress={() => onSelect(groupClassExternalId)}
      testID={baseTestID}
    >
      <VStack flexGrow={1} gap={12} justifyContent="space-between">
        <VStack gap={12}>
          <HStack alignItems="center" justifyContent="space-between">
            {IllustrationIcon}
            {isCore && (
              <Box>
                <CoreTag
                  testID={joinTestIDs(
                    baseTestID,
                    ExploreTestID.GroupClassCardCoreTag,
                  )}
                />
              </Box>
            )}
          </HStack>
          <Text
            testID={joinTestIDs(baseTestID, TextTestID.Title)}
            variant="bodyMedium"
          >
            {title}
          </Text>
          {description && (
            <Text
              color={ColorToken.NeutralSecondary}
              numberOfLines={3}
              testID={joinTestIDs(
                baseTestID,
                ExploreTestID.GroupClassCardDescription,
              )}
              variant="body"
            >
              {description}
            </Text>
          )}
        </VStack>
        {isRegistered && (
          <RegisteredFooter
            baseTestID={baseTestID}
            nextDateString={nextDateString}
            recurrenceString={recurrenceString}
          />
        )}
        {!isRegistered && (
          <OptionsFooter
            baseTestID={baseTestID}
            numberOfOptions={numberOfOptions}
          />
        )}
      </VStack>
    </BaseCard>
  );
};

export type { GroupClassCardItem, GroupClassCardProps };
export default GroupClassCard;
